import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatCard } from "@/components/common";
import { numberID, rupiah } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gauge, TrendingDown, TrendingUp, AlertTriangle, Layers, Scissors } from "lucide-react";

const kg = (v) => Number(v || 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NA = <span className="text-xs italic text-slate-400">Belum tersedia</span>;

function VarianceBadge({ status, pct }) {
  if (!status) return NA;
  const m = {
    FAVORABLE: { label: "Favorable", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: TrendingDown },
    UNFAVORABLE: { label: "Unfavorable", cls: "bg-rose-100 text-rose-700 border-rose-200", Icon: TrendingUp },
    ON_TARGET: { label: "On Target", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Gauge },
  }[status];
  const Icon = m.Icon;
  return <Badge variant="outline" className={m.cls} data-testid={`variance-${status}`}><Icon className="mr-1 h-3 w-3" />{m.label}{pct != null ? ` ${pct}%` : ""}</Badge>;
}

export default function ActualCosting() {
  const [cwEnabled, setCwEnabled] = useState(null);
  const [rows, setRows] = useState(null);
  const [period, setPeriod] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/purchasing/catch-weight/config").then((r) => setCwEnabled(!!r.data.fabric_catch_weight_enabled)).catch(() => setCwEnabled(false)); }, []);
  useEffect(() => {
    if (!cwEnabled) return;
    api.get("/costing/catch-weight/actual-costing").then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
    api.get("/costing/catch-weight/period-analysis", { params: { group_by: "fabric" } }).then((r) => setPeriod(r.data)).catch(() => {});
  }, [cwEnabled]);

  const openDetail = (id) => api.get(`/costing/catch-weight/actual-costing/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiError(e)));

  if (cwEnabled === null) return <Loading />;
  if (!cwEnabled) return (
    <div><PageHeader title="Aktual vs Standar (Kain Catch Weight)" subtitle="Analitik biaya kain aktual." testId="actual-costing-header" />
      <EmptyState title="Fitur Catch Weight tidak aktif" subtitle="Analitik ini hanya tersedia saat fitur Catch Weight aktif." icon={Gauge} /></div>
  );

  const ov = period?.overall;
  return (
    <div data-testid="actual-costing-page">
      <PageHeader title="Aktual vs Standar (Kain Catch Weight)" subtitle="Biaya kain aktual dari rekonsiliasi Cutting (read-only). Approved HPP tidak diubah." testId="actual-costing-header" />

      {ov && ov.batch_count > 0 && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="period-overall">
          <StatCard label="Batch Cutting" value={numberID(ov.batch_count)} icon={Scissors} accent="indigo" />
          <StatCard label="Total Used KG" value={kg(ov.total_used_kg)} icon={Layers} accent="sky" />
          <StatCard label="Total Waste KG" value={kg(ov.total_waste_kg)} icon={AlertTriangle} accent="amber" />
          <StatCard label="Good Output (pcs)" value={numberID(ov.total_good_pcs)} icon={Gauge} accent="emerald" />
          <StatCard label="Weighted g/pcs" value={ov.weighted_actual_grams_per_pcs != null ? ov.weighted_actual_grams_per_pcs : "—"} icon={Gauge} accent="slate" />
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? (
          <EmptyState title="Belum ada data aktual" subtitle="Selesaikan rekonsiliasi Cutting catch-weight untuk melihat analitik biaya aktual." icon={Gauge} />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>FRI / PO</TableHead><TableHead>Kain</TableHead><TableHead className="text-right">Good</TableHead>
              <TableHead className="text-right">Used KG</TableHead><TableHead className="text-right">Waste KG</TableHead><TableHead className="text-right">Return KG</TableHead>
              <TableHead className="text-right">Actual g/pcs</TableHead><TableHead className="text-right">Actual Cost</TableHead>
              <TableHead className="text-right">Actual Rp/pcs</TableHead><TableHead className="text-right">Standar Rp/pcs</TableHead>
              <TableHead className="text-right">Variance</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.recon_id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(r.recon_id)} data-testid={`actual-row-${r.fri_no}`}>
                  <TableCell><p className="font-mono text-xs font-semibold text-indigo-700">{r.fri_no}</p><p className="text-[11px] text-slate-400">{r.po_prod_no || "tanpa PO"}</p></TableCell>
                  <TableCell className="text-xs text-slate-600">{r.fabric_name || "-"}{r.color_name ? ` · ${r.color_name}` : ""}</TableCell>
                  <TableCell className="text-right">{r.good_output_available ? numberID(r.good_output_qty) : NA}</TableCell>
                  <TableCell className="text-right font-semibold">{kg(r.used_weight_kg)}</TableCell>
                  <TableCell className="text-right text-amber-600">{kg(r.waste_weight_kg)}</TableCell>
                  <TableCell className="text-right text-slate-500">{kg(r.returned_weight_kg)}</TableCell>
                  <TableCell className="text-right">{r.actual_grams_per_piece != null ? r.actual_grams_per_piece : NA}</TableCell>
                  <TableCell className="text-right">{rupiah(r.actual_used_fabric_cost)}</TableCell>
                  <TableCell className="text-right font-semibold">{r.actual_fabric_cost_per_piece != null ? rupiah(r.actual_fabric_cost_per_piece) : NA}</TableCell>
                  <TableCell className="text-right">{r.standard_fabric_cost_per_piece != null ? rupiah(r.standard_fabric_cost_per_piece) : <span className="text-[11px] italic text-slate-400">{r.standard?.reason || "HPP standar belum tersedia"}</span>}</TableCell>
                  <TableCell className="text-right">{r.variance_rp != null ? <span className={r.variance_rp < 0 ? "text-emerald-600" : r.variance_rp > 0 ? "text-rose-600" : ""}>{rupiah(r.variance_rp)}</span> : NA}</TableCell>
                  <TableCell><VarianceBadge status={r.variance_status} pct={r.variance_pct} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {period?.outliers?.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="outliers">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-700"><AlertTriangle className="h-4 w-4" /> Batch Perlu Ditinjau (analitis, tidak mengubah HPP)</p>
          <ul className="space-y-1 text-xs text-amber-800">
            {period.outliers.map((o, i) => <li key={i}>{o.fri_no}: {o.flag === "HIGH_WASTE" ? `Waste tinggi ${o.waste_pct}%` : `Biaya ${o.variance_pct}% di atas standar`}</li>)}
          </ul>
        </div>
      )}

      {detail && <DetailDialog d={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function DetailDialog({ d, onClose }) {
  const Row = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-2"><span className="text-xs text-slate-500">{label}</span><span className="text-sm font-semibold text-slate-800">{value}</span></div>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="actual-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">{d.fri_no} · {d.fabric_name} {d.color_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-6">
          <div><Row label="Good Output" value={d.good_output_available ? `${numberID(d.good_output_qty)} pcs` : "Belum tersedia"} />
            <Row label="Used / Waste / Return" value={`${kg(d.used_weight_kg)} / ${kg(d.waste_weight_kg)} / ${kg(d.returned_weight_kg)} KG`} />
            <Row label="Utilization" value={d.utilization_pct != null ? `${d.utilization_pct}%` : "—"} />
            <Row label="Waste %" value={d.waste_pct != null ? `${d.waste_pct}%` : "—"} /></div>
          <div><Row label="Actual g/pcs" value={d.actual_grams_per_piece != null ? d.actual_grams_per_piece : "Belum tersedia"} />
            <Row label="Good pcs / KG used" value={d.good_pcs_per_kg_used != null ? d.good_pcs_per_kg_used : "—"} />
            <Row label="Actual Fabric Cost" value={rupiah(d.actual_used_fabric_cost)} />
            <Row label="Waste Cost (terpisah)" value={rupiah(d.actual_waste_cost)} /></div>
        </div>
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 text-sm">
          <div className="flex items-center justify-between"><span className="text-slate-600">Actual Fabric Cost / pcs</span><b>{d.actual_fabric_cost_per_piece != null ? rupiah(d.actual_fabric_cost_per_piece) : "Belum tersedia"}</b></div>
          <div className="flex items-center justify-between"><span className="text-slate-600">Standar Fabric Cost / pcs</span><b>{d.standard_fabric_cost_per_piece != null ? rupiah(d.standard_fabric_cost_per_piece) : (d.standard?.reason || "HPP standar belum tersedia")}</b></div>
          <div className="flex items-center justify-between"><span className="text-slate-600">Variance</span><span>{d.variance_rp != null ? <><b className={d.variance_rp < 0 ? "text-emerald-600" : "text-rose-600"}>{rupiah(d.variance_rp)}</b> <VarianceBadge status={d.variance_status} pct={d.variance_pct} /></> : "Belum tersedia"}</span></div>
          {d.standard?.weight_basis && <p className="mt-1 text-[11px] text-slate-400">Bobot standar: {d.standard.weight_basis === "qc_passed" ? "good output per SKU" : "target qty per SKU"}. Actual per-size: belum tersedia.</p>}
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Rincian Roll (snapshot cost saat issue)</p>
          <div className="rounded-lg border border-slate-200 overflow-x-auto">
            <Table><TableHeader><TableRow><TableHead>Roll</TableHead><TableHead className="text-right">Used KG</TableHead><TableHead className="text-right">Waste KG</TableHead><TableHead className="text-right">Cost/KG</TableHead><TableHead className="text-right">Used Cost</TableHead><TableHead className="text-right">Waste Cost</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{(d.lines || []).map((l, i) => (
                <TableRow key={i} data-testid={`actual-line-${l.roll_no}`}>
                  <TableCell className="font-mono text-xs text-indigo-700">{l.roll_no}</TableCell>
                  <TableCell className="text-right">{kg(l.used_weight_kg)}</TableCell>
                  <TableCell className="text-right text-amber-600">{kg(l.waste_weight_kg)}</TableCell>
                  <TableCell className="text-right text-xs">{rupiah(l.unit_cost_per_kg)}</TableCell>
                  <TableCell className="text-right font-semibold">{rupiah(l.used_cost)}</TableCell>
                  <TableCell className="text-right">{rupiah(l.waste_cost)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{l.final_status}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
